import { CommonModule } from "@angular/common";
import { NgModule } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { HexConduitModule } from "@hex/angular/conduit";

import { FeatureFlagsModule } from "../../feature-flags/feature-flags.module";
import { CommonUtilsModule } from "../common-utils/common-utils.module";
import { ModifiersModule } from "../presentational/modifiers/modifiers.module";
import { StatefulContainerModule } from "../stateful-container/stateful-container.module";
import { CommonFormComponent } from "./common-form.component";
import { ConduitMenuSizerDirective } from "./conduit-menu-sizer.directive";
import { ConduitScrollAnchorDirective } from "./conduit-scroll-anchor.directive";
import { FieldControlDirective } from "./field-control.directive";
import { CommonFieldManagerDirective } from "./field-manager.directive";
import { FocusRefDirective } from "./focus-ref.directive";
import { FormActiveRef } from "./form-active-ref.service";
import { CommonFormManagerDirective } from "./form-manager.directive";

import { AttachmentTypeSelectorModule } from "../attachment-type-selector/attachment-type-selector.module";
import { ChoiceFieldActionsDirective } from "./fields/choicefield-actions.directive";
import { CommonCheckboxGroupActionsDirective, CommonCheckboxGroupComponent } from "./fields/common-checkbox-group";
import { CommonDropdownActionsDirective, CommonDropdownComponent } from "./fields/common-dropdown";
import { CommonFileUploadActionsDirective, CommonFileUploadComponent } from "./fields/common-fileupload";
import { CommonMultiselectActionsDirective, CommonMultiselectComponent } from "./fields/common-multiselect";
import { CommonRadioGroupActionsDirective, CommonRadioGroupComponent } from "./fields/common-radio-group";
import { CommonReadonlyFieldComponent } from "./fields/common-readonly-field";
import { CommonSwitchActionsDirective, CommonSwitchComponent } from "./fields/common-switch";
import { CommonTextfieldActionsDirective, CommonTextfieldComponent } from "./fields/common-textfield";
import { CommonFieldHintComponent } from "./fields/hint.component";

import { CancelFormCtaDirective, CommonClearStateComponent, CommonFormCtasComponent, EditFormCtaDirective, NewFormCtaDirective, SaveFormCtaDirective } from "./form-ctas";

import { ToggleFieldGroupComponent } from "./field-toggle/field-toggle-group.component";
import { ToggleFieldComponent } from "./field-toggle/field-toggle.component";
import { NoneControlDirective } from "./field-toggle/none-control.directive";
import { DisabledFieldsStrategyDirective } from "./field-toggle/strategies/disabled-fields-strategy.directive";
import { HiddenFieldsStrategyDirective } from "./field-toggle/strategies/hidden-fields-strategy.directive";
import { MultiControlDisabledFieldsStrategyDirective } from "./field-toggle/strategies/multi-control-disabled-fields-strategy.directive";
import { NoneToggleStrategyDirective } from "./field-toggle/strategies/none-toggle-strategy.directive";
import { ToggleControlComponent } from "./field-toggle/toggle-control.component";

import { AggregateRuntimesDirective } from "./auto-calculations/aggregate-runtimes.directive";
import { CalculateSegmentsDirective } from "./auto-calculations/segments.directive";

import { CommonFieldGroupComponent, CommonFieldGroupListComponent, CommonGroupButtonControlDirective } from "./field-group-list";

import { AudioConfigurationsComponent, SeparateAudioFilesComponent, VideoProfilePresetterComponent } from "./special-fields";

import { BreakLengthPipe } from "../../pipes/break-length.pipe";
import { RuntimePipe } from "../../pipes/runtime.pipe";
import { BreakLengthValidator } from "./breaklength-validator";
import { ChangeRouteOnFormCloseDirective } from "./form-close-directives/change-route.directive";
import { NavBackOnFormCloseDirective } from "./form-close-directives/nav-back.directive";
import { RuntimeValidator } from "./runtime-validator"
import { formCtasStatePropProvider, formFieldGroupStatePropProvider, formStatePropProvider } from "./state-props";
import { TemplatePresetterDirective } from "./template-presetter/template-presetter.directive";
import { ClientErrorBoxComponent } from "./validation/client-error-box/client-error-box.component";

@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        CommonUtilsModule,
        HexConduitModule,
        FeatureFlagsModule,
        ModifiersModule,
        StatefulContainerModule,
        AttachmentTypeSelectorModule,
    ],
    declarations: [
        FocusRefDirective,
        CommonFieldManagerDirective,
        FieldControlDirective,
        CommonFieldHintComponent,
        ChoiceFieldActionsDirective,
        CommonFieldGroupListComponent,
        CommonGroupButtonControlDirective,
        CommonFieldGroupComponent,
        CommonFormCtasComponent,
        CommonClearStateComponent,
        NewFormCtaDirective,
        EditFormCtaDirective,
        SaveFormCtaDirective,
        CancelFormCtaDirective,
        CommonCheckboxGroupComponent,
        CommonCheckboxGroupActionsDirective,
        CommonDropdownComponent,
        CommonDropdownActionsDirective,
        CommonFileUploadComponent,
        CommonFileUploadActionsDirective,
        CommonFormComponent,
        CommonFormManagerDirective,
        CommonMultiselectComponent,
        CommonMultiselectActionsDirective,
        CommonRadioGroupComponent,
        CommonRadioGroupActionsDirective,
        CommonSwitchComponent,
        CommonSwitchActionsDirective,
        CommonTextfieldComponent,
        CommonTextfieldActionsDirective,
        CommonReadonlyFieldComponent,
        ToggleControlComponent,
        ToggleFieldGroupComponent,
        ToggleFieldComponent,
        DisabledFieldsStrategyDirective,
        NoneToggleStrategyDirective,
        MultiControlDisabledFieldsStrategyDirective,
        HiddenFieldsStrategyDirective,
        NoneControlDirective,
        ClientErrorBoxComponent,
        TemplatePresetterDirective,
        AudioConfigurationsComponent,
        SeparateAudioFilesComponent,
        VideoProfilePresetterComponent,
        AggregateRuntimesDirective,
        CalculateSegmentsDirective,
        ChangeRouteOnFormCloseDirective,
        NavBackOnFormCloseDirective,
        ConduitScrollAnchorDirective,
        ConduitMenuSizerDirective,
    ],
    exports: [
        FocusRefDirective,
        CommonFieldManagerDirective,
        FieldControlDirective,
        CommonFieldHintComponent,
        ChoiceFieldActionsDirective,
        CommonFieldGroupListComponent,
        CommonGroupButtonControlDirective,
        CommonFieldGroupComponent,
        CommonFormCtasComponent,
        CommonClearStateComponent,
        NewFormCtaDirective,
        EditFormCtaDirective,
        SaveFormCtaDirective,
        CancelFormCtaDirective,
        CommonCheckboxGroupComponent,
        CommonCheckboxGroupActionsDirective,
        CommonDropdownComponent,
        CommonDropdownActionsDirective,
        CommonFileUploadComponent,
        CommonFileUploadActionsDirective,
        CommonFormComponent,
        CommonFormManagerDirective,
        CommonMultiselectComponent,
        CommonMultiselectActionsDirective,
        CommonRadioGroupComponent,
        CommonRadioGroupActionsDirective,
        CommonSwitchComponent,
        CommonSwitchActionsDirective,
        CommonTextfieldComponent,
        CommonTextfieldActionsDirective,
        CommonReadonlyFieldComponent,
        ToggleControlComponent,
        ToggleFieldGroupComponent,
        ToggleFieldComponent,
        DisabledFieldsStrategyDirective,
        NoneToggleStrategyDirective,
        MultiControlDisabledFieldsStrategyDirective,
        HiddenFieldsStrategyDirective,
        NoneControlDirective,
        ClientErrorBoxComponent,
        TemplatePresetterDirective,
        AudioConfigurationsComponent,
        SeparateAudioFilesComponent,
        VideoProfilePresetterComponent,
        AggregateRuntimesDirective,
        CalculateSegmentsDirective,
        ChangeRouteOnFormCloseDirective,
        NavBackOnFormCloseDirective,
        ConduitScrollAnchorDirective,
        ConduitMenuSizerDirective,
    ],
    providers: [
        FocusRefDirective,
        formStatePropProvider,
        formCtasStatePropProvider,
        formFieldGroupStatePropProvider,
        FormActiveRef,
        BreakLengthValidator,
        RuntimeValidator,
        BreakLengthPipe,
        RuntimePipe,
    ],
})
export class CommonFormModule {
}
